// wishlist-page.js

class WishlistPage {
  constructor() {
    // --- Configuration ---
    this.supabaseUrl = "https://penkozrfaxruujxpotdn.supabase.co";
    this.supabaseKey =
      "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBlbmtvenJmYXhydXVqeHBvdGRuIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTM5NDIyNzYsImV4cCI6MjA2OTUxODI3Nn0.peEO1604jU2dxZD2ZQ1OYLUsxs8sNmK-7MIUQZ8E7Rw";
    // -------------------

    this.container = document.getElementById("wishlist-container");
    if (!this.container) return;

    this.customerId = this.container.dataset.customerId;
    this.grid = this.container.querySelector("#wishlist-grid");
    this.loader = this.container.querySelector("#wishlist-loader");
    this.emptyState = this.container.querySelector("#wishlist-empty");
    this.isLoggedIn = !!this.customerId;
    this.localStorageKey = "shopify_wishlist";

    // Initialize Supabase client only if user is logged in
    if (this.isLoggedIn && typeof supabase !== "undefined") {
      this.sbClient = supabase.createClient(this.supabaseUrl, this.supabaseKey);
    }

    this.init();
  }

  async init() {
    if (!this.grid) return;

    console.log("Wishlist page: Starting initialization");

    const wishlistHandles = await this.getWishlistHandles();
    console.log("Wishlist page: Retrieved handles:", wishlistHandles);

    if (wishlistHandles.length === 0) {
      console.log("Wishlist page: No items in wishlist, showing empty state");
      this.loader.style.display = "none";
      this.emptyState.style.display = "block";
      return;
    }

    await this.fetchAndRenderProducts(wishlistHandles);
  }

  // Get wishlist handles from database (logged in) or localStorage (logged out)
  async getWishlistHandles() {
    if (this.isLoggedIn) {
      console.log("Getting wishlist handles from database...");
      return await this.getWishlistFromDatabase();
    } else {
      console.log("Getting wishlist handles from localStorage...");
      return this.getWishlistFromLocalStorage();
    }
  }

  // Get wishlist from database (for logged-in users)
  async getWishlistFromDatabase() {
    if (!this.sbClient) {
      console.warn(
        "Supabase client not initialized, falling back to localStorage"
      );
      return this.getWishlistFromLocalStorage();
    }

    try {
      const { data, error } = await this.sbClient
        .from("wishlist_items")
        .select("product_handle")
        .eq("customer_id", this.customerId)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching wishlist from database:", error);
        return this.getWishlistFromLocalStorage();
      }

      return data.map((item) => item.product_handle);
    } catch (error) {
      console.error("Error in getWishlistFromDatabase:", error);
      return this.getWishlistFromLocalStorage();
    }
  }

  // Get wishlist from localStorage (for logged-out users or fallback)
  getWishlistFromLocalStorage() {
    try {
      const stored = localStorage.getItem(this.localStorageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error("Error loading wishlist from localStorage:", error);
    }
    return [];
  }

  // Remove a product from the wishlist (database or localStorage)
  async removeFromWishlist(handle) {
    if (this.isLoggedIn && this.sbClient) {
      // Remove from database
      const { error } = await this.sbClient
        .from("wishlist_items")
        .delete()
        .match({
          customer_id: this.customerId,
          product_handle: handle,
        });

      if (error) {
        console.error("Error removing from wishlist database:", error);
        this.showToast("Failed to remove item", "error");
        return false;
      }
    } else {
      // Remove from localStorage
      const handles = this.getWishlistFromLocalStorage();
      const index = handles.indexOf(handle);
      if (index > -1) {
        handles.splice(index, 1);
        localStorage.setItem(this.localStorageKey, JSON.stringify(handles));
      }
    }

    return true;
  }

  async fetchAndRenderProducts(handles) {
    console.log("Fetching and rendering products for handles:", handles);
    try {
      this.grid.innerHTML = "";
      for (const handle of handles) {
        try {
          console.log(`Fetching product: ${handle}`);
          // Fetch product data
          const res = await fetch(`/products/${handle}.js`);
          if (res.ok) {
            const product = await res.json();
            console.log(`Successfully fetched product: ${handle}`, product);
            const card = this.createProductCard(product);
            this.grid.appendChild(card);
          } else {
            console.warn(
              `Failed to fetch product ${handle}, status: ${res.status}`
            );
          }
        } catch (err) {
          console.error(`Error fetching product ${handle}`, err);
        }
      }

      console.log("Hiding loader, rendered cards:", this.grid.children.length);
      this.loader.style.display = "none";

      if (this.grid.children.length === 0) {
        console.log("No cards rendered, showing empty state");
        this.emptyState.style.display = "block";
        return;
      }

      // Attach event listeners to card buttons
      this.attachCardButtonListeners();
    } catch (error) {
      console.error("Error fetching and rendering products:", error);
      this.loader.style.display = "none";
      this.emptyState.style.display = "block";
    }
  }

  createProductCard(product) {
    const card = document.createElement("div");
    card.className = "product-card";

    const price = new Intl.NumberFormat(document.documentElement.lang, {
      style: "currency",
      currency: window.Shopify.currency.active,
    }).format(product.price / 100);

    // Handle different image property formats from Shopify API
    let imageSrc = "";
    let imageAlt = product.title;

    if (product.featured_image) {
      imageSrc = product.featured_image;
    } else if (product.images && product.images.length > 0) {
      imageSrc = product.images[0];
    } else if (product.image) {
      imageSrc = product.image.src || product.image;
    }

    // Check if product is available
    const firstVariant = product.variants && product.variants[0];
    const isAvailable =
      product.available && firstVariant && firstVariant.available;

    console.log("Product data for image:", {
      handle: product.handle,
      featured_image: product.featured_image,
      images: product.images,
      image: product.image,
      available: product.available,
      firstVariant: firstVariant,
    });

    card.innerHTML = `
      <a href="${product.url}" class="product-card__link">
        <div class="product-card__image-wrapper">
          ${
            imageSrc
              ? `<img src="${imageSrc}" 
               alt="${imageAlt}" 
               class="product-card__image"
               loading="lazy">`
              : '<div class="product-card__image--placeholder"></div>'
          }
        </div>
        <h3 class="product-card__title">${product.title}</h3>
        <p class="product-card__price">${price}</p>
      </a>
      <div class="wishlist-page-card-buttons">
        <button class="wishlist-page-remove-btn wishlist-page-btn" 
                data-handle="${product.handle}">
          Remove
        </button>
        <button class="wishlist-page-add-to-cart-btn wishlist-page-btn" 
                data-handle="${product.handle}" 
                data-variant-id="${firstVariant?.id}"
                ${!isAvailable ? "disabled" : ""}>
          ${isAvailable ? "Add To Cart" : "Sold Out"}
        </button>
      </div>
    `;

    return card;
  }

  // Show toast notification
  showToast(message, type = "success") {
    const toast = document.createElement("div");
    toast.textContent = message;
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === "success" ? "#28a745" : "#dc3545"};
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      z-index: 9999;
      font-size: 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      animation: slideIn 0.3s ease-out;
    `;

    document.body.appendChild(toast);

    setTimeout(() => {
      toast.style.animation = "slideOut 0.3s ease-in forwards";
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  // Attach event listeners to the card buttons
  attachCardButtonListeners() {
    // Remove from wishlist buttons
    const removeButtons = this.grid.querySelectorAll(
      ".wishlist-page-remove-btn"
    );
    removeButtons.forEach((button) => {
      button.addEventListener("click", async (e) => {
        e.preventDefault();
        const productHandle = e.target.dataset.handle;

        // Disable button during removal
        button.disabled = true;
        button.textContent = "Removing...";

        // Remove from wishlist (database or localStorage)
        const success = await this.removeFromWishlist(productHandle);

        if (!success) {
          // Re-enable button if removal failed
          button.disabled = false;
          button.textContent = "Remove";
          return;
        }

        // Show toast
        this.showToast("Removed from wishlist");

        // Remove the card from the page
        const card = e.target.closest(".product-card");
        if (card) {
          card.remove();

          // Check if grid is now empty
          if (this.grid.children.length === 0) {
            this.emptyState.style.display = "block";
          }
        }

        // Update the main wishlist button states if the function exists
        if (typeof window.reinitWishlistButtons === "function") {
          window.reinitWishlistButtons();
        }
      });
    });

    // Add to cart buttons (now quick view buttons)
    const addToCartButtons = this.grid.querySelectorAll(
      ".wishlist-page-add-to-cart-btn"
    );
    addToCartButtons.forEach((button) => {
      button.addEventListener("click", async (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Check if button is disabled (sold out)
        if (button.disabled) {
          return;
        }

        const productHandle = e.target.dataset.handle;

        if (!productHandle) {
          this.showToast("Product not available", "error");
          return;
        }

        // Open quick view modal
        if (typeof window.openQuickView === "function") {
          // Set up a one-time listener for cart additions for this specific product
          this.setupCartAddedListener(productHandle);

          window.openQuickView(productHandle);
        } else {
          console.error("Quick view function not available");
          this.showToast("Quick view not available", "error");
        }
      });
    });
  }

  // Set up listener for cart additions from quick view
  setupCartAddedListener(productHandle) {
    const handleCartAdded = async (event) => {
      const { detail } = event;
      const addedProductHandle = detail?.productHandle;

      // Only proceed if we have the product handle in the event detail
      // and it matches the product we're tracking
      if (addedProductHandle && addedProductHandle === productHandle) {
        console.log(
          `Removing ${productHandle} from wishlist after cart addition`
        );

        // Remove from wishlist (database or localStorage)
        await this.removeFromWishlist(productHandle);

        // Show toast for removal
        this.showToast("Removed from wishlist");

        // Remove the card from the page
        const card = this.grid
          .querySelector(`[data-handle="${productHandle}"]`)
          ?.closest(".product-card");
        if (card) {
          card.remove();

          if (this.grid.children.length === 0) {
            this.emptyState.style.display = "block";
          }
        }

        // Update the main wishlist button states if the function exists
        if (typeof window.reinitWishlistButtons === "function") {
          window.reinitWishlistButtons();
        }

        // Remove this event listener
        document.removeEventListener("cart:added", handleCartAdded);
      }
    };

    // Listen for cart additions
    document.addEventListener("cart:added", handleCartAdded);

    // Clean up after 30 seconds if no cart addition occurs
    setTimeout(() => {
      document.removeEventListener("cart:added", handleCartAdded);
    }, 30000);
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new WishlistPage();
});
